import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faSpinner,
  faXmark,
  faCopy,
  faCheck,
  faRotate,
  faBan,
  faFloppyDisk,
  faKey,
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import {
  apiKeysService,
  type ApiKeyRecord,
  type CapabilityCatalogGroup,
} from '../../services/apiKeysService'

type SecretReveal = {
  title: string
  apiKey: string
  warning: string
} | null

function formatTs(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return '—'
  }
}

function toDateInputValue(value: string | null | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

const ApiKeysPanel: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [keys, setKeys] = useState<ApiKeyRecord[]>([])
  const [catalog, setCatalog] = useState<CapabilityCatalogGroup[]>([])
  const [filter, setFilter] = useState('')
  const [collapsedDomains, setCollapsedDomains] = useState<Record<string, boolean>>({})
  const [dirtyById, setDirtyById] = useState<Record<string, string[]>>({})
  const [secretReveal, setSecretReveal] = useState<SecretReveal>(null)
  const [copied, setCopied] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [groups, list] = await Promise.all([apiKeysService.catalog(), apiKeysService.list()])
      setCatalog(groups)
      setKeys(list)
      setDirtyById({})
    } catch (e: any) {
      setError(e?.message || 'Failed to load API keys')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const filteredCatalog = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return catalog
    return catalog
      .map((g) => ({
        ...g,
        capabilities: g.capabilities.filter(
          (c) => c.toLowerCase().includes(q) || g.label.toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.capabilities.length > 0)
  }, [catalog, filter])

  const grantsFor = (key: ApiKeyRecord): string[] => dirtyById[key.id] ?? key.allowedCapabilities

  const setGrant = (keyId: string, capability: string, enabled: boolean) => {
    setDirtyById((prev) => {
      const key = keys.find((k) => k.id === keyId)
      const base = prev[keyId] ?? key?.allowedCapabilities ?? []
      const next = new Set(base)
      if (enabled) next.add(capability)
      else next.delete(capability)
      return { ...prev, [keyId]: [...next].sort() }
    })
  }

  const setGroupGrants = (keyId: string, capabilities: string[], enabled: boolean) => {
    setDirtyById((prev) => {
      const key = keys.find((k) => k.id === keyId)
      const base = prev[keyId] ?? key?.allowedCapabilities ?? []
      const next = new Set(base)
      for (const c of capabilities) {
        if (enabled) next.add(c)
        else next.delete(c)
      }
      return { ...prev, [keyId]: [...next].sort() }
    })
  }

  const isDirty = (keyId: string) => {
    if (!(keyId in dirtyById)) return false
    const key = keys.find((k) => k.id === keyId)
    if (!key) return false
    const a = [...(dirtyById[keyId] || [])].sort().join('\0')
    const b = [...(key.allowedCapabilities || [])].sort().join('\0')
    return a !== b
  }

  const saveGrants = async (keyId: string) => {
    const allowedCapabilities = dirtyById[keyId]
    if (!allowedCapabilities) return
    setSavingId(keyId)
    setError(null)
    try {
      const updated = await apiKeysService.update({ id: keyId, allowedCapabilities })
      setKeys((prev) => prev.map((k) => (k.id === keyId ? updated : k)))
      setDirtyById((prev) => {
        const next = { ...prev }
        delete next[keyId]
        return next
      })
    } catch (e: any) {
      setError(e?.message || 'Failed to save grants')
    } finally {
      setSavingId(null)
    }
  }

  const toggleEnabled = async (key: ApiKeyRecord) => {
    setSavingId(key.id)
    setError(null)
    try {
      const updated = await apiKeysService.update({ id: key.id, enabled: !key.enabled })
      setKeys((prev) => prev.map((k) => (k.id === key.id ? updated : k)))
    } catch (e: any) {
      setError(e?.message || 'Failed to update key')
    } finally {
      setSavingId(null)
    }
  }

  const saveExpiry = async (key: ApiKeyRecord, dateValue: string) => {
    setSavingId(key.id)
    setError(null)
    try {
      const expiresAt = dateValue ? new Date(`${dateValue}T23:59:59.000Z`).toISOString() : null
      const updated = await apiKeysService.update({ id: key.id, expiresAt })
      setKeys((prev) => prev.map((k) => (k.id === key.id ? updated : k)))
    } catch (e: any) {
      setError(e?.message || 'Failed to update expiry')
    } finally {
      setSavingId(null)
    }
  }

  const handleCreate = async () => {
    const name = createName.trim()
    if (!name) return
    setCreating(true)
    setError(null)
    try {
      const result = await apiKeysService.create({ name, allowedCapabilities: [] })
      setKeys((prev) => [result.key, ...prev])
      setCreateOpen(false)
      setCreateName('')
      setSecretReveal({
        title: 'API key created',
        apiKey: result.apiKey,
        warning: result.warning,
      })
      setCopied(false)
    } catch (e: any) {
      setError(e?.message || 'Failed to create API key')
    } finally {
      setCreating(false)
    }
  }

  const handleRotate = async (key: ApiKeyRecord) => {
    if (!window.confirm(`Rotate secret for “${key.name}”? The current key will stop working immediately.`)) {
      return
    }
    setSavingId(key.id)
    setError(null)
    try {
      const result = await apiKeysService.rotate(key.id)
      setKeys((prev) => prev.map((k) => (k.id === key.id ? result.key : k)))
      setSecretReveal({
        title: 'API key rotated',
        apiKey: result.apiKey,
        warning: result.warning,
      })
      setCopied(false)
    } catch (e: any) {
      setError(e?.message || 'Failed to rotate API key')
    } finally {
      setSavingId(null)
    }
  }

  const handleRevoke = async (key: ApiKeyRecord) => {
    if (!window.confirm(`Revoke “${key.name}”? It will no longer authenticate.`)) return
    setSavingId(key.id)
    setError(null)
    try {
      const updated = await apiKeysService.revoke(key.id)
      setKeys((prev) => prev.map((k) => (k.id === key.id ? updated : k)))
    } catch (e: any) {
      setError(e?.message || 'Failed to revoke API key')
    } finally {
      setSavingId(null)
    }
  }

  const copySecret = async () => {
    if (!secretReveal?.apiKey) return
    try {
      await navigator.clipboard.writeText(secretReveal.apiKey)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  const toggleDomain = (domain: string) => {
    setCollapsedDomains((prev) => ({ ...prev, [domain]: !prev[domain] }))
  }

  const grantsByKeyId = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const key of keys) {
      map.set(key.id, new Set(grantsFor(key)))
    }
    return map
  }, [keys, dirtyById])

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <FontAwesomeIcon icon={faSpinner} className="text-2xl animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FontAwesomeIcon icon={faKey} />
            API Keys
          </h2>
          <p className="text-sm text-base-content/70 mt-1 max-w-3xl">
            Machine credentials for devices and extensions. Keys are hashed in the database — the full
            secret is shown only once when created or rotated. Grant access per capability; missing
            grants return 403.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Create API key
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <input
          className="input input-bordered input-sm w-full max-w-md"
          placeholder="Filter capabilities…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <span className="text-xs text-base-content/60">
          {keys.length} key{keys.length === 1 ? '' : 's'} ·{' '}
          {filteredCatalog.reduce((n, g) => n + g.capabilities.length, 0)} capabilities shown
        </span>
      </div>

      {keys.length === 0 ? (
        <div className="alert">
          <span>No API keys yet. Create one to grant device access to capabilities.</span>
        </div>
      ) : (
        <>
        <div className="overflow-x-auto border border-base-300 rounded-lg">
          <table className="table table-sm w-full">
            <thead>
              <tr className="bg-base-200">
                <th>Key</th>
                <th>Prefix</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Last used</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => {
                const dirty = isDirty(key.id)
                const busy = savingId === key.id
                return (
                  <tr key={key.id} className={!key.enabled ? 'opacity-60' : undefined}>
                    <td className="font-semibold whitespace-nowrap">{key.name}</td>
                    <td>
                      <code className="text-xs">lts_{key.keyPrefix}_…</code>
                    </td>
                    <td>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="toggle toggle-xs toggle-success"
                          checked={key.enabled}
                          disabled={busy}
                          onChange={() => void toggleEnabled(key)}
                        />
                        <span className="text-xs">{key.enabled ? 'On' : 'Off'}</span>
                      </label>
                    </td>
                    <td>
                      <input
                        type="date"
                        className="input input-bordered input-xs w-[9.5rem]"
                        defaultValue={toDateInputValue(key.expiresAt)}
                        key={`${key.id}-${key.expiresAt || 'none'}`}
                        disabled={busy}
                        onBlur={(e) => {
                          const next = e.target.value
                          const prev = toDateInputValue(key.expiresAt)
                          if (next !== prev) void saveExpiry(key, next)
                        }}
                      />
                    </td>
                    <td className="text-xs whitespace-nowrap opacity-70">{formatTs(key.lastUsedAt)}</td>
                    <td>
                      <div className="flex flex-wrap gap-1 justify-end">
                        <button
                          type="button"
                          className={`btn btn-xs ${dirty ? 'btn-primary' : 'btn-ghost'}`}
                          disabled={!dirty || busy}
                          onClick={() => void saveGrants(key.id)}
                        >
                          {busy ? (
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                          ) : (
                            <FontAwesomeIcon icon={faFloppyDisk} />
                          )}
                          <span className="ml-1">Save grants</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-xs btn-ghost"
                          disabled={busy}
                          onClick={() => void handleRotate(key)}
                          title="Rotate secret"
                        >
                          <FontAwesomeIcon icon={faRotate} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-xs btn-ghost text-error"
                          disabled={busy || !key.enabled}
                          onClick={() => void handleRevoke(key)}
                          title="Revoke"
                        >
                          <FontAwesomeIcon icon={faBan} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="overflow-auto border border-base-300 rounded-lg bg-base-100 max-h-[min(70vh,44rem)]">
          <table className="table table-xs table-pin-rows w-max min-w-full">
            <thead>
              <tr className="bg-base-200">
                <th className="sticky left-0 z-30 bg-base-200 min-w-[14rem] shadow-[2px_0_0_0_rgba(0,0,0,0.06)]">
                  Capability
                </th>
                {keys.map((key) => (
                  <th
                    key={key.id}
                    className={`border-l border-base-300 text-center px-2 py-2 min-w-[3rem] max-w-[6rem] ${
                      !key.enabled ? 'opacity-60' : ''
                    }`}
                    title={`${key.name} (lts_${key.keyPrefix}_…)`}
                  >
                    <div className="normal-case font-semibold text-xs leading-tight truncate">
                      {key.name}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredCatalog.map((group) => {
                const collapsed = !!collapsedDomains[group.domain]
                return (
                  <React.Fragment key={group.domain}>
                    <tr className="bg-base-200/80">
                      <td className="sticky left-0 z-20 bg-base-200 shadow-[2px_0_0_0_rgba(0,0,0,0.06)]">
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs gap-1 justify-start w-full"
                          onClick={() => toggleDomain(group.domain)}
                          title={collapsed ? 'Expand group' : 'Collapse group'}
                        >
                          <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronDown} />
                          <span className="font-semibold">{group.label}</span>
                          <span className="opacity-60">({group.capabilities.length})</span>
                        </button>
                      </td>
                      {keys.map((key) => {
                        const grants = grantsByKeyId.get(key.id) || new Set<string>()
                        const allOn = group.capabilities.every((c) => grants.has(c))
                        const someOn = group.capabilities.some((c) => grants.has(c))
                        const busy = savingId === key.id
                        return (
                          <td
                            key={`${group.domain}-${key.id}-group`}
                            className="border-l border-base-300 text-center align-middle bg-base-200/80"
                          >
                            <input
                              type="checkbox"
                              className="checkbox checkbox-xs"
                              checked={allOn}
                              ref={(el) => {
                                if (el) el.indeterminate = !allOn && someOn
                              }}
                              disabled={busy}
                              onChange={(e) => setGroupGrants(key.id, group.capabilities, e.target.checked)}
                              title={`Toggle all ${group.label} for ${key.name}`}
                              aria-label={`Toggle all ${group.label} for ${key.name}`}
                            />
                          </td>
                        )
                      })}
                    </tr>
                    {!collapsed &&
                      group.capabilities.map((cap) => (
                        <tr key={cap}>
                          <td className="sticky left-0 z-10 bg-base-100 shadow-[2px_0_0_0_rgba(0,0,0,0.06)]">
                            <code className="text-xs font-mono whitespace-nowrap" title={cap}>
                              {cap}
                            </code>
                          </td>
                          {keys.map((key) => {
                            const grants = grantsByKeyId.get(key.id) || new Set<string>()
                            const busy = savingId === key.id
                            return (
                              <td
                                key={`${cap}-${key.id}`}
                                className={`border-l border-base-300 text-center align-middle ${
                                  !key.enabled ? 'opacity-60' : ''
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  className="checkbox checkbox-sm"
                                  checked={grants.has(cap)}
                                  disabled={busy}
                                  onChange={(e) => setGrant(key.id, cap, e.target.checked)}
                                  aria-label={`${key.name} → ${cap}`}
                                />
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      {createOpen && (
        <div className="modal modal-open">
          <div className="modal-box max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Create API key</h3>
              <button type="button" className="btn btn-sm btn-circle btn-ghost" onClick={() => setCreateOpen(false)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <label className="form-control">
              <span className="label-text font-semibold">Name</span>
              <input
                className="input input-bordered w-full"
                placeholder="e.g. Lobby Kiosk"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                autoFocus
              />
            </label>
            <p className="text-sm text-base-content/70 mt-3">
              The secret is shown once after creation. Capability grants start empty — use the matrix to enable access.
            </p>
            <div className="modal-action">
              <button type="button" className="btn btn-ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={creating || !createName.trim()}
                onClick={() => void handleCreate()}
              >
                {creating ? <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" /> : null}
                Create
              </button>
            </div>
          </div>
          <div className="modal-backdrop bg-black/40" onClick={() => !creating && setCreateOpen(false)} />
        </div>
      )}

      {secretReveal && (
        <div className="modal modal-open">
          <div className="modal-box max-w-lg">
            <h3 className="font-bold text-lg mb-2">{secretReveal.title}</h3>
            <div className="alert alert-warning mb-4">
              <span>{secretReveal.warning} Copy it now — you will not be able to view it again.</span>
            </div>
            <div className="flex gap-2 items-stretch">
              <code className="flex-1 break-all text-xs bg-base-200 p-3 rounded-lg select-all">
                {secretReveal.apiKey}
              </code>
              <button type="button" className="btn btn-square" onClick={() => void copySecret()} title="Copy">
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
              </button>
            </div>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setSecretReveal(null)
                  setCopied(false)
                }}
              >
                I stored this key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ApiKeysPanel
